import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';

import { FunnelService } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'Global Health Funnel';
const MIN_PASSWORD_LENGTH = 8;

const PLAN_KEY_BY_CODE: Record<string, string> = {
  GH_SHIELD:    'shield',
  GH_EXECUTIVE: 'executive',
  GH_LEGACY:    'legacy'
};

/**
 * Global Health "Health Finder" result + account creation (WPGH spec Step 4).
 * The quiz is complete and a plan is recommended; here the soft-logged-in funnel
 * user sets a password to create their dashboard account. On success we
 * establish the SPA session (the magic-link `cid` cookie authenticates /user)
 * and move into the authenticated membership wizard — Step 5 plan selection,
 * Step 6 customer info, the health declaration and payment.
 */
@Component({
  selector: 'app-funnel-result-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-result-page.component.html',
  styleUrls: ['./funnel-result-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelResultPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  error   = signal<string | null>(null);

  recommendedCode = signal<string | null>(null);
  /** Step (1-based) of the last answered quiz question, for the back link. */
  lastStep = signal<number | null>(null);

  password = signal('');
  confirm  = signal('');
  busy     = signal(false);
  pwError  = signal<string | null>(null);

  minLength = MIN_PASSWORD_LENGTH;

  /** Localised name of the recommended plan (e.g. "Legacy") for the headline. */
  recommendedName = computed(() => {
    const code = this.recommendedCode();
    if (!code) return '';
    const key = PLAN_KEY_BY_CODE[code] ?? code.toLowerCase();
    return this.translate.translate(`funnel.plans.${key}.name`) || code;
  });

  canSubmit = computed(() =>
    this.password().length >= MIN_PASSWORD_LENGTH &&
    this.password() === this.confirm() &&
    !this.busy()
  );

  constructor() {
    if (!this.funnel.getSessionToken()) {
      this.redirectToLanding();
      return;
    }

    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          if (session.status !== 'completed' && session.status !== 'purchased') {
            this.loading.set(false);
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz'], { replaceUrl: true });
            return;
          }
          this.recommendedCode.set(session.recommended_product_code ?? null);

          // Existing account (already has a password): don't ask for a password
          // again. The user is soft-logged-in via the magic-link cookie — hydrate
          // the SPA session and go straight to the membership wizard.
          if (session.account_exists) {
            this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'existing_account_continue' });
            this.continueAsExistingUser();
            return;
          }

          this.loading.set(false);
          const answered = Object.keys(session.answers ?? {}).length;
          this.lastStep.set(answered > 0 ? answered : null);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY, action: 'result_shown', name: session.recommended_product_code
          });
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load your result.');
        }
      });
  }

  submitPassword(): void {
    if (!this.canSubmit()) {
      if (this.password() !== this.confirm()) this.pwError.set('mismatch');
      return;
    }

    this.busy.set(true);
    this.pwError.set(null);

    this.funnel.setPassword(this.password())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'account_created' });
          // The `cid` cookie (set on the magic-link click) authenticates /user —
          // hydrate the SPA session so authGuard lets us into the login area.
          this.auth.refreshUser()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: async user => {
                // setAuthState flips isAuthenticated — await it so authGuard
                // sees a logged-in user before we navigate into the login area.
                if (user) { await this.auth.setAuthState(user); }
                this.busy.set(false);
                this.funnel.clearSessionToken();
                this.goToMembershipWizard();
              },
              error: () => { this.busy.set(false); this.goToMembershipWizard(); }
            });
        },
        error: err => {
          this.busy.set(false);
          this.pwError.set(err?.error?.error || 'failed');
        }
      });
  }

  /** Back to the quiz, re-asking the last question (server rewinds the answer). */
  goBackToQuiz(): void {
    const lang = this.i18n.getLanguage() || 'en';
    const step = this.lastStep();
    this.router.navigate(
      ['/', lang, 'quiz'],
      step ? { queryParams: { step } } : {}
    );
  }

  /**
   * Existing-account path: the funnel user already has a password, so we skip
   * the setup form. The magic-link `cid` cookie authenticates /user — hydrate
   * the SPA session and move into the membership wizard. If the cookie is gone
   * (expired), fall back to the login page.
   */
  private continueAsExistingUser(): void {
    this.auth.refreshUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async user => {
          if (!user) { this.redirectToLogin(); return; }
          await this.auth.setAuthState(user);
          this.funnel.clearSessionToken();
          this.goToMembershipWizard();
        },
        error: () => this.redirectToLogin()
      });
  }

  private redirectToLogin(): void {
    this.loading.set(false);
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'login'], { replaceUrl: true });
  }

  private goToMembershipWizard(): void {
    const lang = this.i18n.getLanguage() || 'en';
    const plan = this.recommendedCode();
    // The quiz only ever recommends a protection plan, so the wizard opens on the
    // protection slot; the club membership is added later from the dashboard.
    this.router.navigate(
      ['/', lang, 'membership', 'apply'],
      { replaceUrl: true, queryParams: { category: 'protection', ...(plan ? { plan } : {}) } }
    );
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
